"use client";
import {
  __async
} from "../../../chunk-MMUBH76A.js";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import styled from "styled-components";
import { useExternalWallets } from "../../providers/ExternalWalletContext.js";
import { StyledCpslTileButton } from "../common.js";
import { CpslButton, CpslIcon, CpslInput, CpslText } from "@getpara/react-components";
import { useModalStore, useThemeStore } from "../../stores/index.js";
import { ModalStep } from "../../utils/steps.js";
import { useState } from "react";
import { hasEmbeddedAuth } from "../../utils/authLayoutHelpers.js";
const HAS_MORE_LENGTH = 3;
const ExternalWallets = () => {
  const { wallets, connectExternalWallet } = useExternalWallets();
  const setSelectedExternalWalletId = useModalStore((state) => state.setSelectedExternalWalletId);
  const setStep = useModalStore((state) => state.setStep);
  const showAll = useModalStore((state) => state.step === ModalStep.EX_WALLET_MORE);
  const authLayout = useThemeStore((state) => state.authLayout);
  const [search, setSearch] = useState("");
  const hasMore = wallets.length > HAS_MORE_LENGTH;
  const walletsToShow = showAll || !hasMore ? search ? wallets.filter((w) => w.name.toLowerCase().includes(search.toLowerCase())) : wallets : wallets.slice(0, HAS_MORE_LENGTH);
  const showMoreButton = !showAll && hasMore;
  const handleShowAll = () => {
    setStep(ModalStep.EX_WALLET_MORE);
  };
  const handleParaClick = () => {
    setStep(ModalStep.AUTH_MORE);
  };
  const handleWalletClick = (wallet) => () => {
    setSelectedExternalWalletId(wallet.id);
    setStep(ModalStep.EX_WALLET_SELECTED);
    if (wallet.installed) {
      connectExternalWallet(wallet);
    } else if (wallet.isMobile) {
      connectExternalWallet(wallet, true);
    }
  };
  return /* @__PURE__ */ jsxs(Container, { $maxHeight: showAll, children: [
    showAll && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(SearchInputWrapper, { children: /* @__PURE__ */ jsx(
        SearchInput,
        {
          placeholder: "Search for your wallet",
          onCpslInput: (e) => __async(void 0, null, function* () {
            setSearch(e.target.value);
          }),
          value: search,
          style: { width: "100%" },
          children: /* @__PURE__ */ jsx(SearchIcon, { slot: "start", icon: "search" })
        }
      ) }),
      hasEmbeddedAuth(authLayout) && /* @__PURE__ */ jsx(CpslButton, { fullWidth: true, variant: "tertiary", onClick: handleParaClick, children: /* @__PURE__ */ jsxs(WalletButtonOuterContainer, { children: [
        /* @__PURE__ */ jsxs(WalletButtonInnerContainer, { children: [
          /* @__PURE__ */ jsx(CpslIcon, { slot: "start", icon: "paraIcon" }),
          /* @__PURE__ */ jsx(CpslText, { weight: "medium", children: "Para" })
        ] }),
        /* @__PURE__ */ jsx(Badge, { $show: true, $variant: "installed", children: /* @__PURE__ */ jsx(CpslText, { variant: "body2XS", weight: "medium", children: "Available" }) })
      ] }) })
    ] }),
    walletsToShow.map(
      (wallet) => showAll ? /* @__PURE__ */ jsx(CpslButton, { fullWidth: true, variant: "tertiary", onClick: handleWalletClick(wallet), children: /* @__PURE__ */ jsxs(WalletButtonOuterContainer, { children: [
        /* @__PURE__ */ jsxs(WalletButtonInnerContainer, { children: [
          /* @__PURE__ */ jsx(CpslIcon, { slot: "start", src: wallet.iconUrl }),
          /* @__PURE__ */ jsx(CpslText, { weight: "medium", children: wallet.name })
        ] }),
        /* @__PURE__ */ jsx(Badge, { $show: wallet.isMobile || wallet.installed, $variant: wallet.installed ? "installed" : "mobile", children: /* @__PURE__ */ jsx(CpslText, { variant: "body2XS", weight: "medium", children: wallet.installed ? "Installed" : "Mobile" }) })
      ] }) }, wallet.id) : /* @__PURE__ */ jsx(WalletTileButton, { src: wallet.iconUrl, onClick: handleWalletClick(wallet), children: /* @__PURE__ */ jsxs(TileButtonInnerContainer, { children: [
        wallet.installed && /* @__PURE__ */ jsx(InstalledIndicator, {}),
        /* @__PURE__ */ jsx(CpslText, { variant: "bodyXS", color: "secondary", weight: "medium", children: wallet.name })
      ] }) }, wallet.id)
    ),
    showMoreButton && /* @__PURE__ */ jsxs(CpslButton, { variant: "tertiary", fullWidth: true, onClick: handleShowAll, children: [
      /* @__PURE__ */ jsx(CpslIcon, { slot: "start", icon: "wallet" }),
      "More Wallets"
    ] }),
    showAll && /* @__PURE__ */ jsx(BlurContainer, {})
  ] });
};
const Container = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;

  max-height: ${({ $maxHeight }) => $maxHeight ? "348px" : "none"};
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;
const WalletTileButton = styled(StyledCpslTileButton)`
  flex: 1;
`;
const WalletButtonOuterContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
`;
const WalletButtonInnerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  cpsl-icon {
    --icon-color: var(--cpsl-color-contrast);
  }
  cpsl-text {
    &::part(text-element) {
      color: var(--cpsl-color-contrast);
    }
  }
`;
const Badge = styled.div`
  visibility: ${({ $show }) => $show ? "visible" : "hidden"};
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid;
  border-color: ${({ $variant }) => $variant === "installed" ? "var(--cpsl-color-utility-green)" : "var(--cpsl-color-text-primary)"};
  cpsl-text {
    &::part(text-element) {
      color: ${({ $variant }) => $variant === "installed" ? "var(--cpsl-color-utility-green)" : "var(--cpsl-color-text-primary)"};
    }
  }
`;
const InstalledIndicator = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 100%;
  background-color: var(--cpsl-color-utility-green);
`;
const TileButtonInnerContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;
const SearchIcon = styled(CpslIcon)`
  --icon-color: var(--cpsl-color-contrast);
`;
const SearchInputWrapper = styled.div`
  width: 100%;
  background-color: var(--cpsl-color-background-0);

  position: sticky;
  top: 0;
  padding-bottom: 4px;
  margin-bottom: -4px;
`;
const SearchInput = styled(CpslInput)`
  width: 100%;
  --container-background-color: var(--cpsl-color-background-8);
  --input-background-color: var(--cpsl-color-background-8);
`;
const BlurContainer = styled.div`
  position: sticky;
  height: 56px;
  width: 100%;
  bottom: 0;

  background: linear-gradient(0deg, var(--cpsl-color-background-0) 0%, rgba(234, 239, 211, 0) 100%);
  pointer-events: none;
`;
export {
  ExternalWallets
};
